// =============================================================================
// 백업 세트를 한국 시간 날짜로 묶는다 (2026-09-22 대교: "목록이 매우 길게 나열되어
// 있어. 접을 수 있게 만들어줘 날짜별로").
//
// 보존 정책이 DB 28세대 + 미디어 7세대라 표가 항상 35행이었다. 날짜 그룹으로 접으면
// 오늘·어제만 펼쳐 두고 나머지는 헤더 한 줄(건수·업로드 결과)로 읽힌다.
// 화면 컴포넌트와 분리한 순수 함수 — 날짜 경계(KST 자정)와 정렬을 테스트로 고정한다.
// =============================================================================

export type BackupSetLike = {
  id: string;
  kind: 'db' | 'media';
  createdAt: string;
  remote: { status: 'not-configured' | 'pending' | 'success' | 'error' };
};

export type BackupSetGroup<T extends BackupSetLike> = {
  /** 'YYYY-MM-DD' (Asia/Seoul). 파싱 불가 시각은 'unknown'. */
  date: string;
  /** 사람이 읽는 라벨 — '9월 21일 (일) · 오늘' */
  label: string;
  sets: T[];
  counts: { total: number; db: number; media: number; uploaded: number; failed: number; pending: number };
};

const TIME_ZONE = 'Asia/Seoul';
export const UNKNOWN_DATE = 'unknown';

const keyFormat = new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' });
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

/** ISO 시각 → 한국 날짜 키. 파싱 불가면 UNKNOWN_DATE. */
export function kstDateKey(iso: string): string {
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return UNKNOWN_DATE;
  // en-CA 는 YYYY-MM-DD 로 찍힌다 — 문자열 정렬이 곧 날짜 정렬.
  return keyFormat.format(new Date(time));
}

function relativeWord(date: string, now: Date): string | null {
  const today = kstDateKey(now.toISOString());
  if (date === today) return '오늘';
  const yesterday = kstDateKey(new Date(now.getTime() - 86_400_000).toISOString());
  if (date === yesterday) return '어제';
  return null;
}

/** '9월 21일 (일)' + 오늘/어제 표시. */
export function dateGroupLabel(date: string, now: Date = new Date()): string {
  if (date === UNKNOWN_DATE) return '날짜 확인 불가';
  // Intl 의 ko-KR 요일 표기는 런타임마다 달라서 직접 조립한다. KST 정오 = 03:00Z 라 UTC 요일이 곧 KST 요일.
  const [, month, day] = date.split('-');
  const weekday = WEEKDAYS[new Date(`${date}T12:00:00+09:00`).getUTCDay()] ?? '';
  const base = `${Number(month)}월 ${Number(day)}일 (${weekday})`;
  const word = relativeWord(date, now);
  return word ? `${base} · ${word}` : base;
}

/**
 * 시간 역순으로 정렬한 뒤 날짜별로 묶는다. 그룹 순서도 최신 날짜가 앞.
 * 파싱 불가 시각은 'unknown' 그룹으로 맨 뒤에 모은다 — 감추면 세트가 사라진 것으로 보인다.
 */
export function groupBackupSetsByDate<T extends BackupSetLike>(sets: readonly T[], now: Date = new Date()): BackupSetGroup<T>[] {
  const sorted = [...sets].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const groups = new Map<string, BackupSetGroup<T>>();
  for (const set of sorted) {
    const date = kstDateKey(set.createdAt);
    let group = groups.get(date);
    if (!group) {
      group = { date, label: dateGroupLabel(date, now), sets: [], counts: { total: 0, db: 0, media: 0, uploaded: 0, failed: 0, pending: 0 } };
      groups.set(date, group);
    }
    group.sets.push(set);
    group.counts.total += 1;
    group.counts[set.kind] += 1;
    if (set.remote.status === 'success') group.counts.uploaded += 1;
    else if (set.remote.status === 'error') group.counts.failed += 1;
    else if (set.remote.status === 'pending') group.counts.pending += 1;
  }
  return [...groups.values()].sort((a, b) => {
    if (a.date === UNKNOWN_DATE) return 1;
    if (b.date === UNKNOWN_DATE) return -1;
    return b.date.localeCompare(a.date);
  });
}

/** 기본으로 펼쳐 둘 그룹 — 오늘과 어제. 둘 다 없으면 가장 최근 그룹 하나. */
export function defaultOpenDates<T extends BackupSetLike>(groups: readonly BackupSetGroup<T>[], now: Date = new Date()): Set<string> {
  const open = new Set<string>();
  const today = kstDateKey(now.toISOString());
  const yesterday = kstDateKey(new Date(now.getTime() - 86_400_000).toISOString());
  for (const group of groups) if (group.date === today || group.date === yesterday) open.add(group.date);
  if (!open.size && groups[0]) open.add(groups[0].date);
  return open;
}

/** 접힌 헤더 한 줄 — '9월 21일 (일) · 오늘 · DB 4 · 미디어 1 · 업로드 5/5'. 클라우드 미설정이면 업로드 항목을 뺀다. */
export function dateGroupSummary<T extends BackupSetLike>(group: BackupSetGroup<T>, remoteConfigured: boolean): string {
  const parts = [`DB ${group.counts.db}`, `미디어 ${group.counts.media}`];
  if (remoteConfigured) {
    parts.push(`업로드 ${group.counts.uploaded}/${group.counts.total}`);
    if (group.counts.failed) parts.push(`실패 ${group.counts.failed}`);
  }
  return parts.join(' · ');
}
